import type { IQuiz, IAttemptResult, IUserAnswer } from '~/components/widget/quiz/types'

interface StartAttemptResponse {
  status: string
  message: string
  data: { attempt_id: string }
}

interface QuizResponse {
  data: IQuiz
}

interface ResultsResponse {
  status: string
  message: string
  data: IAttemptResult
}

export const useQuiz = (quizId: number) => {
  const { $api } = useNuxtApp()
  const fingerprintStore = useFingerprintStore()

  const quiz = ref<IQuiz | null>(null)
  const questions = ref<IQuiz['questions']>([])
  const attemptId = ref<string | null>(null)

  const ensureFingerprint = async () => {
    if (!fingerprintStore.loaded) await fingerprintStore.initializeFingerprint()
  }

  const guestParams = () =>
    fingerprintStore.visitorId ? { fingerprint: fingerprintStore.visitorId } : {}

  const checkAttempt = async (): Promise<boolean> => {
    await ensureFingerprint()
    const url = useUrlWithParams(`/quizzes/${quizId}/attempts`, guestParams())
    const response = await $api<StartAttemptResponse>(url)
    if (response.data.attempt_id) {
      attemptId.value = response.data.attempt_id
      return true
    }
    return false
  }

  const startAttempt = async (): Promise<void> => {
    await ensureFingerprint()

    const response = await $api<StartAttemptResponse>(`/quizzes/${quizId}/attempts`, {
      method: 'POST',
      body: { ...guestParams() },
    })

    attemptId.value = response.data.attempt_id
  }

  const fetchQuestion = async (questionId?: number) => {
    if (!attemptId.value) return

    const url = useUrlWithParams(`/quizzes/${quizId}`, {
      attempt_id: attemptId.value,
      ...(questionId != null ? { question_id: questionId } : {}),
      ...guestParams(),
    })
    const response = await $api<QuizResponse>(url)
    quiz.value = response.data
    questions.value = response.data.questions
  }

  const submitAnswer = async (answer: IUserAnswer): Promise<void> => {
    if (!attemptId.value) return

    await $api(`/attempts/${attemptId.value}/answers`, {
      method: 'POST',
      body: { ...answer, ...guestParams() },
    })
  }

  const getResults = async (): Promise<IAttemptResult> => {
    if (!attemptId.value) throw new Error('No active attempt')

    const url = useUrlWithParams(`/attempts/${attemptId.value}/results`, guestParams())
    const response = await $api<ResultsResponse>(url)
    return response.data
  }

  const claimFingerprint = async (): Promise<void> => {
    if (!fingerprintStore.visitorId) return
    await $api('/claim-fingerprint', {
      method: 'POST',
      body: { fingerprint: fingerprintStore.visitorId },
    })
  }

  const resetAttempt = (): void => {
    quiz.value = null
    questions.value = []
    attemptId.value = null
  }

  return {
    quiz,
    questions,
    checkAttempt,
    startAttempt,
    fetchQuestion,
    submitAnswer,
    getResults,
    claimFingerprint,
    resetAttempt,
  }
}

export default useQuiz
